import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit, Optional, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import { GetStoreRevisionInfoQuery, WebPresenceConfigData, } from '../../../common/generated-types';
import { NGXLogger } from 'ngx-logger';
import { parseMaybeJsonList, parseMaybeJsonValue } from '../../../common/utils/safe-json-parser';
import { NavMenuOption, LayoutOptionData, MarketingBannerData, SiteStyleData } from '../../../common/interfaces';
import { Meta, Title } from '@angular/platform-browser';
import { StateService } from '../../../core/providers/state/state.service';
import { stripHtml } from '../../../common/utils/strip-html';
import { replaceHostname } from '../../../common/utils/url-handlers';
import { getRoundedClass, computeLayoutStyles } from '../../../common/utils/styles-helper';
import { CanonicalService } from '../../../core/providers/canonical/canonical.service';
import { isPlatformBrowser } from '@angular/common';
import { WAIT_FOR_NAVIGATION_INTERVAL, WAIT_FOR_NAVIGATION_TIMEOUT } from '../../../common/constants';
import { CarouselArrowType, SiteStyleType } from '../../../common/enums';
import { AssetPreviewPipe } from '../../../shared/pipes/asset-preview.pipe';
import { navigateByNavigationItem } from '../../../common/utils/navigation-helpers';

@Component({
  selector: 'vsf-general-page',
  templateUrl: './general-page.component.html',
  styleUrls: ['./general-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeneralPageComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();
  storeInfo: GetStoreRevisionInfoQuery['storeInfo'];
  baseUrl: string;
  identifier: string;
  sellerChannelId: string;
  withHeaderMessage: boolean = true;
  shopHost: string;
  pageName: string;

  private stateSub: Subscription;
  previewPipe = new AssetPreviewPipe();
  layoutOrder: LayoutOptionData[] = [];
  navigationMenu: NavMenuOption[] = [];
  webPresenceConfig: WebPresenceConfigData | null;

  heroSection: any | null;

  pageComponents: any[];
  collectionForSeriesHeroSectionList: any[];

  isMobile: boolean | null = null;
  readyToDisplay$ = new BehaviorSubject<boolean>(false);

  showFixedBanner: boolean = false;
  isHeaderFloating: boolean = false;
  showTopNav: boolean = true;

  waitForNavigationInterval = WAIT_FOR_NAVIGATION_INTERVAL;
  waitForNavigationTimeout = WAIT_FOR_NAVIGATION_TIMEOUT;
  pdpAssetsArrowType: CarouselArrowType = CarouselArrowType.LargeChevron;
  h1PageHeadingIndex: number = -1;
  h1CustomText: string = '';
  h1StyleMode: string = 'auto';
  h1CustomStyles: { [key: string]: string } = {};

  constructor(private titleService: Title,
              private route: ActivatedRoute,
              private router: Router,
              private metaService: Meta,
              private changeDetectorRef: ChangeDetectorRef,
              private canonicalService: CanonicalService,
              @Inject(PLATFORM_ID) private platformId: object,
              private stateService: StateService,
              @Optional() @Inject('STORE_IDENTIFIER') public storeIdentifierFromSSR: string | null,
              private logger: NGXLogger) { }

  async ngOnInit() {
      const routeDataSub = this.route.data;
      const storeInfoSub = this.stateService.select((state:any) => state.storeInfo).pipe(takeUntil(this.destroy$), filter(notNullOrUndefined));
      const isMobileSub = this.stateService.select((state:any) => state.isMobile).pipe(takeUntil(this.destroy$), filter(notNullOrUndefined));
      const storePathSub = this.stateService.select((state:any) => state.storePath).pipe(takeUntil(this.destroy$), filter(notNullOrUndefined));
      const shopHostSub = this.stateService.select((state:any) => state.shopHost).pipe(takeUntil(this.destroy$), filter(notNullOrUndefined));
      const hasTopMessage$ = this.stateService.select(state => state.hasTopMessage).pipe(takeUntil(this.destroy$), filter(notNullOrUndefined));
      this.stateSub = this.stateService.select(state => ({
        isHeaderFloating: state.isHeaderFloating,
      })).subscribe(({ isHeaderFloating }) => {
        if(isHeaderFloating !== this.isHeaderFloating) {
            this.isHeaderFloating = isHeaderFloating;
            this.changeDetectorRef.markForCheck();
        }
      });
      await combineLatest([
        routeDataSub, storeInfoSub, isMobileSub, storePathSub, shopHostSub, hasTopMessage$
      ]).pipe(takeUntil(this.destroy$)).subscribe(([routeData, storeInfo, isMobile, storePath, shopHost, hasTopMessage]) => {
        this.baseUrl = storePath;
        this.storeInfo = storeInfo;
        this.identifier = storeInfo?.identifier || '';
        this.isMobile = isMobile;
        this.shopHost = shopHost;
        if(this.withHeaderMessage !== hasTopMessage) {
          this.withHeaderMessage = hasTopMessage;
        }
        const siteStyles = parseMaybeJsonValue<SiteStyleData>(this.storeInfo?.siteStyles, this.logger);
        this.pdpAssetsArrowType = siteStyles?.pdpAssetsArrowType || CarouselArrowType.LargeChevron;
        const pageData = routeData['pageData'];
        if(pageData) {
          this.pageName = pageData.pageName;
          this.webPresenceConfig = pageData.webPresence;
          this.navigationMenu = parseMaybeJsonList<NavMenuOption>(pageData.navigationMenu, this.logger);
          this.logger.debug(`[GeneralPageComponent]navigationMenu: ${this.navigationMenu}`);
          this.pageComponents = pageData.pageComponentList || [];
          this.layoutOrder = this.pageComponents.map((pc:any) => {
            const layoutOption = { ...pc.layoutOption } as LayoutOptionData;
            if (layoutOption) {
              // Compute desktop width percentage
              layoutOption.widthPercentage = layoutOption.width && layoutOption.width > 0
                ? `${layoutOption.width * 100}%`
                : '100%';
              // Compute mobile width percentage
              layoutOption.mobileWidthPercentage = layoutOption.mobileWidth && layoutOption.mobileWidth > 0
                ? `${layoutOption.mobileWidth * 100}%`
                : '100%';
              // Ensure margin properties are numbers (fallback to 0 if undefined)
              layoutOption.topMargin = layoutOption.topMargin || 0;
              layoutOption.bottomMargin = layoutOption.bottomMargin || 0;
              layoutOption.mobileTopMargin = layoutOption.mobileTopMargin || 0;
              layoutOption.mobileBottomMargin = layoutOption.mobileBottomMargin || 0;
              layoutOption.horizontalPadding = layoutOption.horizontalPadding || 0;
              layoutOption.mobileHorizontalPadding = layoutOption.mobileHorizontalPadding || 0;
              layoutOption.topPadding = layoutOption.topPadding || 0;
              layoutOption.bottomPadding = layoutOption.bottomPadding || 0;
              layoutOption.mobileTopPadding = layoutOption.mobileTopPadding || 0;
              layoutOption.mobileBottomPadding = layoutOption.mobileBottomPadding || 0;
              if (this.isMobile) {
                layoutOption.linePadding = `${layoutOption.mobileTopPadding}px ${layoutOption.mobileHorizontalPadding}px
                    ${layoutOption.mobileBottomPadding}px`;
              } else {
                layoutOption.linePadding = `${layoutOption.topPadding}px ${layoutOption.horizontalPadding}px
                    ${layoutOption.bottomPadding}px`;
              }
              const activeRounded = this.isMobile
                ? (layoutOption.mobileSectionRoundedCorners ?? layoutOption.sectionRoundedCorners)
                : layoutOption.sectionRoundedCorners;
              layoutOption.roundedClass = getRoundedClass(activeRounded);
            }
            return layoutOption;
          }).filter(notNullOrUndefined);
        }
        this.sellerChannelId = this.storeInfo?.sellerChannel?.id || '';

        if(isPlatformBrowser(this.platformId)) {
          window.addEventListener('scroll', () => {
              const scrolled = window.scrollY || document.documentElement.scrollTop;
              let showTopNav = true;
              // If scrolled beyond a certain point, hide top nav => show floating nav
              if (scrolled > 150) {
                  showTopNav = false;
              }
              if(showTopNav !== this.showTopNav) {
                  this.showTopNav = showTopNav;
              }
          });
          this.route.queryParams.subscribe((params) => {
            const section = params['section'];
            if (section) {
              this.scrollToSectionAfterLoad(section);
              // Clear the 'section' parameter so it won't be present on subsequent navigations
              this.router.navigate([], {
                queryParams: { section: null },
                queryParamsHandling: 'merge'
              });
            }
          });
        }
        this.computeLayoutStyles();
        this.computeH1Heading();
        if(this.webPresenceConfig) {
          this.updatePageMetadata(this.webPresenceConfig);
        }
        const canonicalURL = pageData.slug.toLowerCase();
        this.canonicalService.setCanonicalURL(`${shopHost}${canonicalURL}`);
        this.readyToDisplay$.next(true);
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy() {
    if (this.stateSub) {
      this.stateSub.unsubscribe();
    }
    this.canonicalService.removeCanonicalURL();
    this.removeMetadata();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.changeDetectorRef.markForCheck();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.computeLayoutStyles();
  }

  scrollToSection(item: NavMenuOption): void {
    navigateByNavigationItem(this.router, item, '/');
  }

  scrollToSectionAfterLoad(sectionId: string): void {
    const interval = setInterval(() => {
      const targetEl = document.getElementById(sectionId);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth' });
        clearInterval(interval);
      }
    }, this.waitForNavigationInterval);
    setTimeout(() => clearInterval(interval), this.waitForNavigationTimeout);
  }

  private updatePageMetadata(webPresenceConfig: WebPresenceConfigData): void {
    // SEO title/description
    const seoTitleHtml = webPresenceConfig?.title || this.pageName || this.storeInfo?.name || '';
    const seoDescriptionHtml = webPresenceConfig.description || this.storeInfo?.description || '';
    const seoTitle = stripHtml(seoTitleHtml, this.platformId);
    const seoDescription = stripHtml(seoDescriptionHtml, this.platformId);
    // Open Graph title/description
    const ogTitleSameAsSeo = webPresenceConfig?.openGraphTitleSameAsSeoTitle ?? true;
    const ogDescSameAsSeo = webPresenceConfig?.openGraphDescriptionSameAsSeoDescription ?? true;
    const ogTitleHtml = ogTitleSameAsSeo ? seoTitleHtml : (webPresenceConfig?.openGraphTitle || seoTitleHtml);
    const ogDescriptionHtml = ogDescSameAsSeo ? seoDescriptionHtml : (webPresenceConfig?.openGraphDescription || seoDescriptionHtml);
    const ogTitle = stripHtml(ogTitleHtml, this.platformId);
    const ogDescription = stripHtml(ogDescriptionHtml, this.platformId);

    const imageUrl = this.previewPipe.transform(webPresenceConfig?.metaAsset, 600, 315) || '';
    // Set page title/description
    this.titleService.setTitle(seoTitle);
    this.metaService.updateTag({ name: 'description', content: seoDescription });
    // Set Open Graph meta tags
    this.metaService.updateTag({ property: 'og:title', content: ogTitle });
    this.metaService.updateTag({ property: 'og:description', content: ogDescription });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:url', content: `${this.shopHost}${this.baseUrl}` });
    // If StoreHeroImages is available and is an array, use the first image
    this.metaService.updateTag({ property: 'og:image', content: `${this.shopHost}${this.baseUrl}${imageUrl}` });
    // Set Twitter Card meta tags
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: ogTitle  });
    this.metaService.updateTag({ name: 'twitter:description', content: ogDescription });
    // If StoreHeroImages is available and is an array, use the first image
    if (webPresenceConfig?.metaAsset && webPresenceConfig?.metaAsset.type === 'IMAGE') {
      this.metaService.updateTag({ name: 'twitter:image', content: `${this.shopHost}${this.baseUrl}${imageUrl}` });
    }
    // If there's a hero video, you might want to include it in the metadata
    if (webPresenceConfig?.metaAsset && webPresenceConfig?.metaAsset.type === 'VIDEO') {
      this.metaService.updateTag({ property: 'og:video', content: webPresenceConfig?.metaAsset.source });
    }
  }

  private removeMetadata() {
    this.metaService.removeTag('name="description"');
    this.metaService.removeTag('property="og:title"');
    this.metaService.removeTag('property="og:description"');
    this.metaService.removeTag('property="og:type"');
    this.metaService.removeTag('property="og:url"');
    this.metaService.removeTag('property="og:image"');
    this.metaService.removeTag('property="og:video"');
    this.metaService.removeTag('name="twitter:card"');
    this.metaService.removeTag('name="twitter:title"');
    this.metaService.removeTag('name="twitter:description"');
    this.metaService.removeTag('name="twitter:image"');
  }

  /**
   * Determines which section (if any) should render its title as an H1 tag.
   * - 'custom': render an explicit H1 above sections with h1Text
   * - 'auto' (default): first section with a visible title gets H1
   * - 'none': no H1 management
   */
  private computeH1Heading(): void {
    this.h1PageHeadingIndex = -1;
    this.h1CustomText = '';
    this.h1StyleMode = this.webPresenceConfig?.h1StyleMode || 'auto';
    this.h1CustomStyles = this.computeH1Styles();
    const h1Source = this.webPresenceConfig?.h1Source || 'auto';
    if (h1Source === 'none') {
      return;
    }
    if (h1Source === 'custom') {
      this.h1CustomText = this.webPresenceConfig?.h1Text || '';
      return;
    }
    // 'auto': find first section with a visible title
    if (this.pageComponents?.length > 0) {
      this.h1PageHeadingIndex = this.pageComponents.findIndex(
        (pc: any) => pc.title || pc.subtitle || pc.content?.title,
      );
    }
  }

  private computeH1Styles(): { [key: string]: string } {
    if (this.h1StyleMode !== 'custom') {
      return {};
    }
    const styles: { [key: string]: string } = {};
    const fontSize = this.isMobile
      ? this.webPresenceConfig?.h1MobileFontSize
      : this.webPresenceConfig?.h1FontSize;
    if (fontSize) {
      styles['font-size'] = `${fontSize}px`;
    }
    if (this.webPresenceConfig?.h1Color) {
      styles['color'] = this.webPresenceConfig.h1Color;
    }
    if (this.webPresenceConfig?.h1Alignment) {
      styles['text-align'] = this.webPresenceConfig.h1Alignment;
    }
    return styles;
  }

  computeLayoutStyles() {
    computeLayoutStyles(this.layoutOrder, this.isMobile);
  }
}
